import React, { useState, useEffect, useCallback } from 'react';
import { connect } from '../../redux/store';
import { actions } from '../../redux/modules/submission';
import { I18n } from '@kineticdata/react';
import {
  Card,
  CardCol,
  CardRow,
  InfoMessage,
  TimeAgo,
  C,
  openModalForm,
} from '@kineticdata/bundle-common';
import { Collapse } from 'reactstrap';
import classNames from 'classnames';

const getStatusBadgeColor = (status = '') => {
  switch (status) {
    case 'Resolved':
      return 'badge-success';
    case 'Cancelled':
      return 'badge-dark';
    case 'Being Addressed':
      return 'badge-warning';
    default:
      return 'badge-info';
  }
};

const IssueNode = connect(
  (state, props) => ({
    profile: state.app.profile,
    submission: state.submission.data,
  }),
  {
    fetchSubmission: actions.fetchSubmissionRequest,
  },
)(
  ({
    className,
    issue,
    isOpen,
    toggle,
    isMobile,
    profile,
    submission,
    fetchSubmission,
  }) => {
    const assignedToYou =
      (issue.values[C.FIELD_ASSIGNED_INDIVIDUALS] || []).includes(
        profile.username,
      ) ||
      (issue.values[C.FIELD_ASSIGNED_TEAMS] || []).some(team =>
        profile.memberships.map(m => m.team && m.team.name).includes(team),
      );
    const isIssueForCurrentSubmission =
      submission &&
      (submission.type === C.REQUEST_FORM_TYPE
        ? submission.id === issue.parent.id
        : submission.parent && submission.parent.id === issue.parent.id);
    const resolveIssue = useCallback(
      options => () =>
        openModalForm({
          ...C.ISSUE_RESOLVE_FORM_CONFIG,
          submissionId: issue.id,
          duration: 5000,
          updated: () => {
            if (isIssueForCurrentSubmission) {
              fetchSubmission(submission.id);
            }
          },
          ...options,
        }),
      [issue.id, submission.id, isIssueForCurrentSubmission, fetchSubmission],
    );

    const statusRow = issue.values['Status'] && (
      <CardRow className="card__row--status">
        <span
          className={classNames(
            'badge badge-stylized text-muted',
            getStatusBadgeColor(issue.values['Status']),
          )}
        >
          <I18n>{issue.values['Status']}</I18n>
        </span>
      </CardRow>
    );
    const titleRow = (
      <CardRow type="subtitle">
        <I18n>{issue.values['Issue Summary']}</I18n>
      </CardRow>
    );
    const metaRow = (
      <CardRow type="meta" className="text-muted">
        <span>
          <I18n>Created</I18n>{' '}
        </span>
        <strong>
          <TimeAgo timestamp={issue.createdAt} />
        </strong>
        {assignedToYou &&
          !['Completed', 'Cancelled'].includes(issue.values['Status']) && (
            <>
              <span>{' \u00b7 '}</span>
              <span>
                <I18n>Assigned to you</I18n>
              </span>
            </>
          )}
      </CardRow>
    );
    const toggleMoreRow = typeof toggle === 'function' && (
      <CardRow className="card__row--toggle">
        <button className="btn btn-text" onClick={toggle}>
          <span
            className={classNames('fa fa-fw fa-md text-muted', {
              'fa-plus': !isOpen,
              'fa-minus': isOpen,
            })}
          />
          <span>
            <I18n>{isOpen ? 'Show Less' : 'Show More'}</I18n>
          </span>
        </button>
      </CardRow>
    );
    return (
      <li className={classNames('activity__row', className)}>
        <Card>
          <CardRow>
            {isMobile ? (
              <>
                <CardCol>
                  {statusRow}
                  {titleRow}
                  {metaRow}
                  {toggleMoreRow}
                </CardCol>
              </>
            ) : (
              <>
                <CardCol>
                  {titleRow}
                  {metaRow}
                </CardCol>
                <CardCol className="card__col--append-meta">
                  {statusRow}
                  {toggleMoreRow}
                </CardCol>
              </>
            )}
          </CardRow>
          <Collapse tag={CardCol} isOpen={isOpen} toggle={toggle}>
            <CardRow>
              <dl>
                {issue.values['Issuer Name'] && (
                  <div>
                    <dt>
                      <span className={'title'}>
                        <I18n>Issued By</I18n>
                      </span>
                    </dt>
                    <dd>
                      <I18n>{issue.values['Issuer Name']}</I18n>
                    </dd>
                  </div>
                )}
                {issue.values['Related To'] && (
                  <div>
                    <dt>
                      <span className={'title'}>
                        <I18n>Related To</I18n>
                      </span>
                    </dt>
                    <dd>
                      <I18n>{issue.values['Related To']}</I18n>
                    </dd>
                  </div>
                )}
                <div>
                  <dt>
                    <span className={'title'}>
                      <I18n>Assigned To</I18n>
                    </span>
                  </dt>
                  <dd>
                    <I18n>
                      {issue.values['Assigned Individual Display Name'] ||
                        issue.values['Assigned Team Display Name']}
                    </I18n>
                  </dd>
                </div>
                {issue.values['Updated By Display Name'] && (
                  <div>
                    <dt>
                      <span className={'title'}>
                        <I18n>Updated By</I18n>
                      </span>
                    </dt>
                    <dd>
                      <I18n>{issue.values['Updated By Display Name']}</I18n>
                    </dd>
                  </div>
                )}
                {issue.values['Issue Detail'] && (
                  <div>
                    <dt>
                      <span className={'title'}>
                        <I18n>Details</I18n>
                      </span>
                    </dt>
                    <dd>
                      <I18n>{issue.values['Issue Detail']}</I18n>
                    </dd>
                  </div>
                )}
                {issue.coreState !== C.CORE_STATE_DRAFT && (
                  <>
                    {issue.values['Completed Display Names'] && (
                      <div>
                        <dt>
                          <span className={'title'}>
                            <I18n>Resolved By</I18n>
                          </span>
                        </dt>
                        <dd>
                          <I18n>{issue.values['Completed Display Names']}</I18n>
                        </dd>
                      </div>
                    )}
                    {issue.values['Resolution'] && (
                      <div>
                        <dt>
                          <span className={'title'}>
                            <I18n>Resolution</I18n>
                          </span>
                        </dt>
                        <dd>
                          <I18n>{issue.values['Resolution']}</I18n>
                        </dd>
                      </div>
                    )}
                  </>
                )}
              </dl>
            </CardRow>
            {issue.coreState === C.CORE_STATE_DRAFT && (
              <>
                {assignedToYou && (
                  <>
                    {issue.values['Status'] === 'Pending' && (
                      <CardRow>
                        <button
                          className="btn btn-tertiary"
                          onClick={resolveIssue({
                            loaded: form =>
                              form
                                .getFieldByName('Action Status')
                                .value('Being Addressed'),
                          })}
                        >
                          <I18n>Flag Issue as Being Addressed</I18n>
                        </button>
                      </CardRow>
                    )}
                    <CardRow>
                      <button
                        className="btn btn-tertiary"
                        onClick={resolveIssue({
                          loaded: form =>
                            form
                              .getFieldByName('Action Status')
                              .value('Completed'),
                        })}
                      >
                        <I18n>Resolve Issue</I18n>
                      </button>
                    </CardRow>
                  </>
                )}
              </>
            )}
            {toggleMoreRow}
          </Collapse>
        </Card>
      </li>
    );
  },
);

const ToggleAllButton = ({ isOpen, toggle }) => (
  <button className="ml-auto btn btn-text" onClick={toggle}>
    <span
      className={classNames('fa fa-fw fa-md text-muted', {
        'fa-plus': !isOpen,
        'fa-minus': isOpen,
      })}
    />
    <span>
      <I18n>{!isOpen ? 'Expand All' : 'Collapse All'}</I18n>
    </span>
  </button>
);

export const IssuesList = ({ issues, handleAddIssue }) => {
  // Get count of issues
  const issuesCount = issues.length;
  // State for tracking which issues are expanded
  const [openIssuesList, setOpenIssuesList] = useState(
    new Array(issuesCount).fill(false),
  );
  // Size of state array for tracking expanded issues
  const openIssuesCount = openIssuesList.length;
  // FLag checking if all issues are expanded
  const areAllIssuesOpen = !openIssuesList.some(o => !o);
  // Effect that rebuilds the expanded issues array state if the number of
  // issues changes due to polling and more being fetched
  useEffect(
    () => {
      if (issuesCount > openIssuesCount) {
        setOpenIssuesList(list => [
          ...list,
          ...new Array(issuesCount - openIssuesCount).fill(false),
        ]);
      }
    },
    [issuesCount, openIssuesCount],
  );
  // Function that returns a handler for toggling an issue at a specific index
  const toggleIssue = i => () =>
    setOpenIssuesList(list => {
      const toggledList = [...list];
      toggledList.splice(i, 1, !list[i]);
      return toggledList;
    });
  // Handler for toggling all issue records
  const toggleAllIssues = () =>
    setOpenIssuesList(new Array(openIssuesCount).fill(!areAllIssuesOpen));

  return (
    <ul className="activity activity--issues" role="tabpanel">
      {issues.length > 0 && (
        <>
          <li className="activity__meta activity__meta--start">
            <ToggleAllButton
              isOpen={areAllIssuesOpen}
              toggle={toggleAllIssues}
            />
          </li>
          <li className="activity__spacer" aria-hidden="true" />
        </>
      )}
      {issues.length > 0 ? (
        issues.map((issue, i) => (
          <IssueNode
            key={issue.id}
            issue={issue}
            isOpen={!!openIssuesList[i]}
            toggle={toggleIssue(i)}
          />
        ))
      ) : (
        <div className="text-muted">
          <InfoMessage title="No issues to display" />
        </div>
      )}
      {(issues.length > 0 || handleAddIssue) && (
        <>
          <li className="activity__spacer" aria-hidden="true" />
          <li className="activity__meta activity__meta--end">
            {handleAddIssue && (
              <button className="btn btn-tertiary" onClick={handleAddIssue}>
                Add Issue
              </button>
            )}
            {issues.length > 0 && (
              <ToggleAllButton
                isOpen={areAllIssuesOpen}
                toggle={toggleAllIssues}
              />
            )}
          </li>
        </>
      )}
    </ul>
  );
};
